import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import {FiUser, FiUsers, FiMail} from "../assets/icons/vander"
import { useDepartures } from "../hooks/useDepartures";
import { createHold, fetchExtras } from "../services/api";

const formatPrice = (amount, currency) => {
    const options = {
        style: 'currency',
        currency,
        minimumFractionDigits: currency === 'CLP' ? 0 : 2,
        maximumFractionDigits: currency === 'CLP' ? 0 : 2,
    };
    return amount.toLocaleString(currency === 'CLP' ? 'es-CL' : 'en-US', options);
};

const availableFromDeparture = (dep) => {
    if (!dep) return 0;
    return (dep.capacity_total || 0) - (dep.capacity_reserved || 0);
};

export default function DetailSidebar({ mapUrl, priceCents, priceCurrency = 'CLP', destination, packageId, depositPercentDefault, title }) {
    const { departures, loading: depLoading, error: depError } = useDepartures(packageId);
    const [extras, setExtras] = useState([]);
    const [extrasLoading, setExtrasLoading] = useState(false);
    const [extrasError, setExtrasError] = useState("");
    const [selectedDepartureId, setSelectedDepartureId] = useState(null);
    const [adults, setAdults] = useState(1);
    const [children, setChildren] = useState(0);
    const [selectedExtras, setSelectedExtras] = useState([]);
    const [error, setError] = useState('');
    const [holdInfo, setHoldInfo] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const navigate = useNavigate();
    const embedUrl = mapUrl || "";

    useEffect(() => {
        if (departures.length && !selectedDepartureId) {
            setSelectedDepartureId(departures[0].id);
        }
    }, [departures, selectedDepartureId]);

    const selectedDeparture = useMemo(
        () => departures.find((d) => d.id === selectedDepartureId),
        [departures, selectedDepartureId]
    );

    const totalPeople = Math.max(0, Number(adults)) + Math.max(0, Number(children));
    // Los montos CLP se manejan en pesos (sin centavos), igual que el precio base.
    const basePrice = priceCurrency === 'CLP' ? (priceCents || 0) : (priceCents || 0) / 100;
    const extrasTotal = useMemo(() => {
        return selectedExtras.reduce((acc, id) => {
            const extra = extras.find((opt) => opt.id === id);
            if (!extra) return acc;
            const price = extra.price_cents || 0; // pesos, sin /100
            const count = extra.per_person ? totalPeople : 1;
            return acc + price * count;
        }, 0);
    }, [selectedExtras, extras, totalPeople]);
    const total = basePrice * totalPeople + extrasTotal;

    // Espejo de resolve_deposit_percent del backend: departure -> package -> default (50).
    // TODO confirmar con negocio: default 50% vs 100%
    const effectiveDepositPercent = selectedDeparture?.deposit_percent ?? depositPercentDefault ?? 50;
    const deposit = total * (effectiveDepositPercent / 100);
    const available = availableFromDeparture(selectedDeparture);

    const toggleExtra = (id) => {
        setSelectedExtras((prev)=>{
            if(prev.includes(id)){
                return prev.filter((x)=>x!==id);
            }
            return [...prev, id];
        })
    };

    useEffect(() => {
        if (!packageId) return;
        setExtrasLoading(true);
        setExtrasError("");
        fetchExtras(packageId, selectedDepartureId)
            .then((data) => {
                setExtras(data || []);
                setExtrasLoading(false);
                setSelectedExtras([]); // reset selección al cambiar
            })
            .catch((err) => {
                setExtrasError(err.message || "No se pudieron cargar los extras.");
                setExtrasLoading(false);
            });
    }, [packageId, selectedDepartureId]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setHoldInfo(null);
        if (!selectedDeparture) {
            setError('Selecciona una fecha de salida.');
            return;
        }
        if (!totalPeople) {
            setError('Ingresa al menos 1 persona.');
            return;
        }
        if (totalPeople > available) {
            setError('No hay cupos suficientes para esta salida.');
            return;
        }
        setSubmitting(true);
        try {
            const hold = await createHold(selectedDeparture.id, { quantity: totalPeople });
            setHoldInfo(hold);
            const selectedExtraNames = extras.filter((e)=>selectedExtras.includes(e.id)).map((e)=>e.name);
            navigate('/cart', {
                state: {
                    packageId,
                    departureId: selectedDeparture.id,
                    holdId: hold.id,
                    holdExpiresAt: hold.expires_at,
                    startDate: selectedDeparture.start_date,
                    endDate: selectedDeparture.end_date,
                    adults: Number(adults),
                    children: Number(children),
                    extras: selectedExtraNames,
                    extrasIds: selectedExtras,
                    currency: priceCurrency,
                    total,
                    depositPercent: hold.deposit_percent ?? effectiveDepositPercent,
                    basePrice,
                    priceCents,
                    destination,
                    title,
                    totalPeople,
                }
            });
        } catch (err) {
            setError(err.message || 'No se pudo reservar cupos.');
        } finally {
            setSubmitting(false);
        }
    };

    return(
        <div className="lg:col-span-4 md:col-span-5">
            <div className="p-4 rounded-md shadow dark:shadow-gray-700 sticky top-20">
                <form onSubmit={handleSubmit}>
                    <div className='flex flex-col'>
                        <label className="font-semibold" id="salidas-label">Salidas disponibles:</label>
                        {depLoading && <p className="text-slate-400 text-sm mt-2">Cargando salidas...</p>}
                        {depError && <p className="text-red-500 text-sm mt-2">{depError}</p>}
                        {!depLoading && !depError && departures.length === 0 && (
                            <p className="text-slate-400 text-sm mt-2">No hay salidas configuradas.</p>
                        )}
                        <div role="group" aria-labelledby="salidas-label" className="flex flex-col gap-2 mt-2 max-h-80 overflow-y-auto pr-1">
                            {departures.slice(0, 20).map((item)=>(
                                <button
                                    type="button"
                                    key={item.id}
                                    onClick={()=>setSelectedDepartureId(item.id)}
                                    className={`px-3 py-2 rounded-md border text-sm text-left ${selectedDepartureId === item.id ? 'bg-red-500 text-white border-red-500' : 'border-gray-200 dark:border-gray-700 text-slate-600 dark:text-slate-300'}`}>
                                    <div className="flex justify-between">
                                        <span>{new Date(item.start_date).toLocaleDateString('es-CL')}</span>
                                        {item.end_date && <span className="text-xs">{new Date(item.end_date).toLocaleDateString('es-CL')}</span>}
                                    </div>
                                    <div className="text-xs mt-1 text-white/90 dark:text-slate-400">
                                        Cupos: {availableFromDeparture(item)}
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="mt-4">
                        <div className="md:flex">
                            <div className="md:w-1/3">
                                <label htmlFor="Noperson" className="font-medium">Adultos:</label>
                            </div>

                            <div className="md:w-2/3 mt-4 md:mt-0">
                                <div className="form-icon relative">
                                    <FiUser className="w-4 h-4 absolute top-3 start-4"></FiUser>
                                    <input type="number" inputMode="numeric" min="0" value={adults} onChange={(e)=>setAdults(e.target.value)} className="w-full ps-12 py-2 px-3 h-10 bg-transparent dark:bg-slate-900 dark:text-slate-200 rounded outline-none border border-gray-100 dark:border-gray-800 focus:ring-2 focus:ring-red-500/40" placeholder="No. de adultos" id="Noperson" name="adultos"/>
                                </div>
                            </div>
                        </div>
                        
                        <div className="md:flex mt-4">
                            <div className="md:w-1/3">
                                <label htmlFor="Nochildren" className="font-medium">Niños:</label>
                            </div>

                            <div className="md:w-2/3 mt-4 md:mt-0">
                                <div className="form-icon relative">
                                    <FiUsers className="w-4 h-4 absolute top-3 start-4"></FiUsers>
                                    <input type="number" inputMode="numeric" min="0" value={children} onChange={(e)=>setChildren(e.target.value)} className="w-full ps-12 py-2 px-3 h-10 bg-transparent dark:bg-slate-900 dark:text-slate-200 rounded outline-none border border-gray-100 dark:border-gray-800 focus:ring-2 focus:ring-red-500/40" placeholder="No. de niños" id="Nochildren" name="ninos"/>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="mt-4">
                        <p className="font-medium mb-2">Extras</p>
                        {extrasLoading && <p className="text-slate-400 text-sm">Cargando extras...</p>}
                        {extrasError && <p className="text-red-500 text-sm">{extrasError}</p>}
                        <div className="space-y-2">
                            {extras.map((opt)=>(
                                <label key={opt.id} className="flex items-center justify-between text-sm">
                                    <div className="flex items-center space-x-2">
                                        <input type="checkbox" checked={selectedExtras.includes(opt.id)} onChange={()=>toggleExtra(opt.id)} />
                                        <span>{opt.name}</span>
                                    </div>
                                    <span className="text-slate-500 dark:text-slate-400">{formatPrice(opt.price_cents||0, 'CLP')}{opt.per_person ? ' / persona' : ''}</span>
                                </label>
                            ))}
                            {!extrasLoading && extras.length === 0 && <p className="text-slate-400 text-sm">Sin extras configurados.</p>}
                        </div>
                    </div>

                    <div className="mt-4 border-t border-gray-100 dark:border-gray-800 pt-4 space-y-1 text-sm">
                        <div className="flex justify-between">
                            <span>Personas</span>
                            <span>{totalPeople}</span>
                        </div>
                        <div className="flex justify-between">
                            <span>Total extras</span>
                            <span>{formatPrice(extrasTotal, 'CLP')}</span>
                        </div>
                        <div className="flex justify-between font-semibold">
                            <span>Total</span>
                            <span>{formatPrice(total, 'CLP')}</span>
                        </div>
                        <div className="flex justify-between text-red-500 font-semibold">
                            <span>Anticipo ({effectiveDepositPercent}%)</span>
                            <span>{formatPrice(deposit, 'CLP')}</span>
                        </div>
                        {holdInfo && (
                            <p className="text-xs text-green-600">Cupo bloqueado hasta {new Date(holdInfo.expires_at).toLocaleTimeString('es-CL')}</p>
                        )}
                    </div>

                    {error && <p className="text-red-500 text-sm mt-2">{error}</p>}

                    <div className="mt-4 space-y-2">
                        <button type="submit" className={`py-2 px-5 inline-block tracking-wide align-middle duration-500 text-base text-center text-white rounded-md w-full ${submitting ? 'bg-red-300 cursor-not-allowed' : 'bg-red-500 hover:bg-red-600'}`} disabled={submitting}>
                            {submitting ? 'Reservando cupo...' : 'Reservar'}
                        </button>
                        <div className="flex space-x-2">
                            <a href="https://wa.me/56966281145" target="_blank" rel="noreferrer" className="w-1/2 py-2 px-3 text-center border border-green-500 text-green-600 rounded-md">WhatsApp</a>
                            <a href="mailto:turismo@cmsystem.cl" className="w-1/2 py-2 px-3 text-center border border-slate-300 dark:border-gray-700 rounded-md flex items-center justify-center space-x-1"><FiMail className="w-4 h-4"/><span>Correo</span></a>
                        </div>
                    </div>
                </form>

                {embedUrl && (
                    <div className="mt-6">
                        <h5 className="text-lg font-medium">Mapa del recorrido</h5>

                        <div className="mt-3">
                            <iframe src={embedUrl} style={{border:'0'}} title="Mapa del recorrido" className="w-full h-[300px] rounded-lg"></iframe>
                        </div>
                    </div>
                )}
            </div>

            <div className="md:hidden fixed bottom-0 start-0 end-0 bg-white dark:bg-slate-900 shadow-[0_-4px_10px_rgba(0,0,0,0.12)] p-3 border-t border-gray-200 dark:border-gray-800" style={{paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom))'}}>
                <div className="flex items-center justify-between">
                    <div>
                        <p className="text-xs text-slate-500">Total (anticipo {effectiveDepositPercent}%)</p>
                        <p className="font-semibold">{formatPrice(total, 'CLP')} / anticipo {formatPrice(deposit, 'CLP')}</p>
                    </div>
                    <div className="flex space-x-2">
                        <a href="https://wa.me/56966281145" target="_blank" rel="noreferrer" className="py-2 px-3 bg-green-500 text-white rounded-md text-sm">WhatsApp</a>
                        <button type="button" onClick={handleSubmit} disabled={submitting} className={`py-2 px-3 text-white rounded-md text-sm ${submitting ? 'bg-red-300 cursor-not-allowed' : 'bg-red-500'}`}>{submitting ? 'Reservando...' : 'Reservar'}</button>
                    </div>
                </div>
            </div>
        </div>
    )
}
